/**
 * Hook useRolePermissions - Gestion des permissions par rôle
 * Restaurant DAMAS - Système de caisse professionnel
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    PERMISSION_TYPES,
    ROLE_PERMISSIONS,
    hasPermission,
    hasRoleLevel,
    getRoleConfig,
    canAccessRegister,
    canAccessTable,
    getAllowedCategories,
    getPermissionsSummary,
} from '../config/rolePermissions';

export const useRolePermissions = () => {
    const { user } = useAuth();
    
    // Configuration du rôle actuel
    const roleConfig = useMemo(() => {
        return getRoleConfig(user?.role);
    }, [user?.role]);
    
    // Vérifier une permission
    const can = useCallback((permission) => {
        if (!user?.role) return false;
        return hasPermission(user.role, permission);
    }, [user?.role]);
    
    // Vérifier plusieurs permissions (toutes requises)
    const canAll = useCallback((permissions) => {
        if (!user?.role || !permissions?.length) return false;
        return permissions.every(p => hasPermission(user.role, p));
    }, [user?.role]);
    
    // Vérifier plusieurs permissions (au moins une)
    const canAny = useCallback((permissions) => {
        if (!user?.role || !permissions?.length) return false;
        return permissions.some(p => hasPermission(user.role, p));
    }, [user?.role]);
    
    // Vérifier le niveau de rôle
    const hasLevel = useCallback((requiredLevel) => {
        return hasRoleLevel(user?.role, requiredLevel);
    }, [user?.role]);
    
    // Vérifier l'accès à une caisse
    const canAccessCaisse = useCallback((register) => {
        return canAccessRegister(user, register);
    }, [user]);
    
    // Vérifier l'accès à une table
    const canAccessTableFn = useCallback((table) => {
        return canAccessTable(user, table);
    }, [user]);
    
    // Filtrer les catégories autorisées
    const filterCategories = useCallback((categories) => {
        return getAllowedCategories(user?.role, categories);
    }, [user?.role]);
    
    // Résumé des permissions
    const permissionsSummary = useMemo(() => {
        return getPermissionsSummary(user?.role);
    }, [user?.role]);
    
    // Raccourcis pour permissions courantes
    const permissions = useMemo(() => ({
        // Caisses
        canOpenRegister: can(PERMISSION_TYPES.REGISTER_OPEN),
        canCloseRegister: can(PERMISSION_TYPES.REGISTER_CLOSE),
        canForceCloseRegister: can(PERMISSION_TYPES.REGISTER_FORCE_CLOSE),
        canViewAllRegisters: can(PERMISSION_TYPES.REGISTER_VIEW_ALL),
        canTransferRegister: can(PERMISSION_TYPES.REGISTER_TRANSFER),
        
        // Commandes
        canCreateOrder: can(PERMISSION_TYPES.ORDER_CREATE),
        canModifyOrder: can(PERMISSION_TYPES.ORDER_MODIFY),
        canCancelOrder: can(PERMISSION_TYPES.ORDER_CANCEL),
        canDeleteOrder: can(PERMISSION_TYPES.ORDER_DELETE),
        canViewAllOrders: can(PERMISSION_TYPES.ORDER_VIEW_ALL),
        canReassignOrder: can(PERMISSION_TYPES.ORDER_REASSIGN),
        
        // Paiements
        canPayCash: can(PERMISSION_TYPES.PAYMENT_CASH),
        canPayCard: can(PERMISSION_TYPES.PAYMENT_CARD),
        canPayMobile: can(PERMISSION_TYPES.PAYMENT_MOBILE),
        canApplyDiscount: can(PERMISSION_TYPES.PAYMENT_DISCOUNT),
        canVoidPayment: can(PERMISSION_TYPES.PAYMENT_VOID),
        canRefund: can(PERMISSION_TYPES.PAYMENT_REFUND),
        
        // Produits
        canViewProducts: can(PERMISSION_TYPES.PRODUCT_VIEW),
        canAddProduct: can(PERMISSION_TYPES.PRODUCT_ADD),
        canEditProduct: can(PERMISSION_TYPES.PRODUCT_EDIT),
        canDeleteProduct: can(PERMISSION_TYPES.PRODUCT_DELETE),
        canViewStock: can(PERMISSION_TYPES.STOCK_VIEW),
        canAdjustStock: can(PERMISSION_TYPES.STOCK_ADJUST),
        
        // Tables
        canViewTables: can(PERMISSION_TYPES.TABLE_VIEW),
        canAssignTable: can(PERMISSION_TYPES.TABLE_ASSIGN),
        canTransferTable: can(PERMISSION_TYPES.TABLE_TRANSFER),
        canViewAllTables: can(PERMISSION_TYPES.TABLE_VIEW_ALL),
        
        // Cuisine/Bar
        canViewKitchen: can(PERMISSION_TYPES.KITCHEN_VIEW),
        canManageKitchen: can(PERMISSION_TYPES.KITCHEN_MANAGE),
        canViewBar: can(PERMISSION_TYPES.BAR_VIEW),
        canManageBar: can(PERMISSION_TYPES.BAR_MANAGE),
        
        // Rapports
        canViewReports: can(PERMISSION_TYPES.REPORT_VIEW),
        canExportReports: can(PERMISSION_TYPES.REPORT_EXPORT),
        canValidateClosing: can(PERMISSION_TYPES.CLOSING_VALIDATE),
        canCorrectClosing: can(PERMISSION_TYPES.CLOSING_CORRECT),
        
        // Admin
        canViewUsers: can(PERMISSION_TYPES.USER_VIEW),
        canManageUsers: can(PERMISSION_TYPES.USER_MANAGE),
        canViewSettings: can(PERMISSION_TYPES.SETTINGS_VIEW),
        canEditSettings: can(PERMISSION_TYPES.SETTINGS_EDIT),
        canSupervise: can(PERMISSION_TYPES.SUPERVISION),
    }), [can]);
    
    // Accès spéciaux basés sur le rôle
    const specialAccess = useMemo(() => ({
        canAccessAllRegisters: roleConfig?.specialAccess?.canAccessAllRegisters || false,
        canAccessAllTables: roleConfig?.specialAccess?.canAccessAllTables || false,
        canOverrideClosings: roleConfig?.specialAccess?.canOverrideClosings || false,
        canDeleteOrders: roleConfig?.specialAccess?.canDeleteOrders || false,
        canModifyPastOrders: roleConfig?.specialAccess?.canModifyPastOrders || false,
        canForceOperations: roleConfig?.specialAccess?.canForceOperations || false,
        canAccessSystemSettings: roleConfig?.specialAccess?.canAccessSystemSettings || false,
        isBarOnly: roleConfig?.specialAccess?.barOnly || false,
        isKitchenOnly: roleConfig?.specialAccess?.kitchenOnly || false,
        isTablesOnly: roleConfig?.specialAccess?.tablesOnly || false,
        allowedCategories: roleConfig?.specialAccess?.allowedCategories || null,
    }), [roleConfig]);
    
    // Infos du rôle
    const roleInfo = useMemo(() => ({
        name: roleConfig?.label || user?.role || 'Inconnu',
        description: roleConfig?.description || '',
        level: roleConfig?.level || 0,
        color: roleConfig?.color || '#6B7280',
        icon: roleConfig?.icon || 'User',
        restrictions: roleConfig?.restrictions || [],
    }), [roleConfig, user?.role]);
    
    // Helpers pour l'UI
    const isAdmin = user?.role === 'ADMIN';
    const isDirecteur = user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';
    const isManager = isAdmin || isDirecteur;
    const isCaissier = user?.role === 'CAISSIER';
    const isServeur = user?.role === 'SERVEUR' || user?.role === 'SERVEUSE';
    const isBarman = user?.role === 'BARMAN';
    const isCuisinier = user?.role === 'CUISINIER';
    
    return {
        // User & Role
        user,
        role: user?.role,
        roleConfig,
        roleInfo,
        
        // Permission checks
        can,
        canAll,
        canAny,
        hasLevel,
        
        // Access checks
        canAccessCaisse,
        canAccessTable: canAccessTableFn,
        filterCategories,
        
        // Shortcuts
        permissions,
        specialAccess,
        permissionsSummary,
        
        // Role helpers
        isAdmin,
        isDirecteur,
        isManager,
        isCaissier,
        isServeur,
        isBarman,
        isCuisinier,
        
        // Constants
        PERMISSION_TYPES,
        ROLE_PERMISSIONS,
    };
};

export default useRolePermissions;
